/* /assets/section-art-wall-shuffle.js
 *
 * "Shuffle the wall" — a desktop-only, one-shot fun interaction for the
 * Art Wall homepage module (sections/section-art-wall-designer-homepage-module.liquid).
 *
 * A small button fades in only when the cursor enters the bottom-right
 * corner of the section. Clicking it rearranges every poster into a new,
 * randomly-generated layout that:
 *   - keeps clear of the left-hand text column,
 *   - never overlaps another poster (so it reads like a real wall you
 *     could hang at home),
 * holds the new layout for 3s, then animates everything back to its
 * original position. One cycle per click.
 *
 * No admin configuration: positions and sizes are read straight off the
 * rendered posters (which already carry their real-cm width + image
 * aspect ratio), so the layout is computed entirely in JS.
 *
 * Degrades gracefully: if JS is off, reduced-motion is requested, or the
 * viewport is below the desktop breakpoint, nothing runs and the static
 * wall is shown exactly as authored.
 */
(function () {
  'use strict';

  var DESKTOP_MIN = 768; // matches the section's CSS breakpoint
  var MOVE_MS = 3000; // travel time each way
  var HOLD_MS = 3000; // dwell time in the scrambled layout
  var CORNER_ZONE_PX = 160; // bottom-right hot zone that reveals the button
  var GAP_PCT = 0.8; // min spacing between posters (% of wall width)
  var EDGE_PCT = 2; // keep posters this far from the wall edges
  var LEFT_GAP_PCT = 2; // breathing room to the right of the text column
  var LEFT_FALLBACK_PCT = 34; // left boundary when no text block is found
  var MAX_TRIES = 150; // random spots tried per poster, per attempt
  var MAX_ATTEMPTS = 400; // full-layout restarts before falling back

  var reduceMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  // Axis-aligned rectangle overlap test, with a uniform gap baked in.
  function overlaps(a, b, gap) {
    return !(
      a.x + a.w + gap <= b.x ||
      b.x + b.w + gap <= a.x ||
      a.y + a.h + gap <= b.y ||
      b.y + b.h + gap <= a.y
    );
  }

  function setup(section) {
    if (section.__awdShuffleBound) return;
    section.__awdShuffleBound = true;

    var wall = section.querySelector('.awd-hp__wall');
    var button = section.querySelector('.awd-hp__shuffle');
    if (!wall || !button) return;

    var posters = Array.prototype.slice.call(
      wall.querySelectorAll('.awd-hp__poster')
    );
    if (posters.length < 2) return;

    var running = false;

    // --- Reveal the button only in the bottom-right corner -------------
    function onMove(e) {
      var rect = section.getBoundingClientRect();
      var nearCorner =
        e.clientX >= rect.right - CORNER_ZONE_PX &&
        e.clientX <= rect.right &&
        e.clientY >= rect.bottom - CORNER_ZONE_PX &&
        e.clientY <= rect.bottom;
      button.classList.toggle('is-visible', nearCorner);
    }
    function onLeave() {
      if (!running) button.classList.remove('is-visible');
    }
    section.addEventListener('mousemove', onMove);
    section.addEventListener('mouseleave', onLeave);
    // Keep it visible while the pointer is on the button itself.
    button.addEventListener('mouseenter', function () {
      button.classList.add('is-visible');
    });

    // --- Measure current geometry as percentages of the wall ----------
    // Read on each run so a window resize / editor reflow is respected.
    function measure() {
      var wallRect = wall.getBoundingClientRect();
      if (!wallRect.width || !wallRect.height) return null;

      // Left boundary: posters stay entirely to the right of the text
      // column (top to bottom), not just clear of the text rectangle, so
      // nothing drifts into the left corner above/below the copy.
      var leftBound = LEFT_FALLBACK_PCT;
      var text = section.querySelector('.awd-hp__text');
      if (text) {
        var tr = text.getBoundingClientRect();
        leftBound =
          ((tr.right - wallRect.left) / wallRect.width) * 100 + LEFT_GAP_PCT;
      }

      var boxes = posters.map(function (el) {
        var r = el.getBoundingClientRect();
        return {
          el: el,
          w: (r.width / wallRect.width) * 100,
          h: (r.height / wallRect.height) * 100,
          // Original top-left anchor, in % of the wall.
          ox: ((r.left - wallRect.left) / wallRect.width) * 100,
          oy: ((r.top - wallRect.top) / wallRect.height) * 100
        };
      });

      // Lowest the bottom edge of any print may reach, as % of wall
      // height. Derived from the lowest authored print so scrambled
      // layouts stay on the wall and never land on the floor/skirting —
      // the merchant's own layout already defines the safe floor line.
      var bottomBound = 0;
      boxes.forEach(function (b) {
        var bottom = b.oy + b.h;
        if (bottom > bottomBound) bottomBound = bottom;
      });

      return { leftBound: leftBound, bottomBound: bottomBound, boxes: boxes };
    }

    // --- Build a non-overlapping scrambled layout ---------------------
    // Full-layout rejection sampling with restarts: each attempt places
    // every print (largest first) into a random spot that clears the
    // text column, the wall edges, the floor line, and every print
    // already placed. If any print can't be placed, the whole attempt is
    // abandoned and we restart — this finds a fully valid layout far more
    // reliably than placing greedily once. Returns true on success.
    function tryPlan(data) {
      var order = data.boxes.slice().sort(function (a, b) {
        return b.w * b.h - a.w * a.h;
      });
      var floor = Math.min(100 - EDGE_PCT, data.bottomBound);

      for (var attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        var placed = [];
        var coords = [];
        var ok = true;

        for (var k = 0; k < order.length; k++) {
          var box = order[k];
          var minX = data.leftBound;
          var maxX = Math.max(minX, 100 - EDGE_PCT - box.w);
          var minY = EDGE_PCT;
          var maxY = Math.max(minY, floor - box.h);

          var found = false;
          for (var i = 0; i < MAX_TRIES; i++) {
            var cand = {
              x: rand(minX, maxX),
              y: rand(minY, maxY),
              w: box.w,
              h: box.h
            };
            var clash = false;
            for (var j = 0; j < placed.length; j++) {
              if (overlaps(cand, placed[j], GAP_PCT)) {
                clash = true;
                break;
              }
            }
            if (!clash) {
              placed.push(cand);
              coords.push({ box: box, x: cand.x, y: cand.y });
              found = true;
              break;
            }
          }
          if (!found) {
            ok = false;
            break;
          }
        }

        if (ok) {
          coords.forEach(function (c) {
            c.box.nx = c.x;
            c.box.ny = c.y;
          });
          return true;
        }
      }
      return false;
    }

    // Guaranteed-safe fallback for the rare case no random layout is
    // found: mirror every print vertically about the bounding box of the
    // current layout. A reflection of a non-overlapping layout is itself
    // non-overlapping, keeps each print's X (so the text stays clear),
    // and stays within the original vertical band (so nothing hits the
    // floor). Different from the original, never broken.
    function mirrorPlan(data) {
      var minY = Infinity;
      var maxY = -Infinity;
      data.boxes.forEach(function (b) {
        if (b.oy < minY) minY = b.oy;
        if (b.oy + b.h > maxY) maxY = b.oy + b.h;
      });
      var sum = minY + maxY;
      data.boxes.forEach(function (b) {
        b.nx = b.ox;
        b.ny = sum - (b.oy + b.h);
      });
    }

    function scrambleLayout(data) {
      if (!tryPlan(data)) mirrorPlan(data);
      return data.boxes;
    }

    function applyPositions(boxes, useNew) {
      boxes.forEach(function (box) {
        box.el.style.left = (useNew ? box.nx : box.ox) + '%';
        box.el.style.top = (useNew ? box.ny : box.oy) + '%';
      });
    }

    // --- One-shot cycle: scramble -> hold -> return -------------------
    function run() {
      if (running) return;
      var data = measure();
      if (!data) return;
      running = true;
      button.setAttribute('disabled', 'disabled');

      var boxes = scrambleLayout(data);

      // Pin originals as inline styles first (so the transition has a
      // concrete start value), then enable transitions and move.
      applyPositions(boxes, false);
      // Force a reflow so the browser registers the start positions.
      void wall.offsetWidth;
      section.classList.add('awd-hp--shuffling');

      requestAnimationFrame(function () {
        applyPositions(boxes, true);
      });

      window.setTimeout(function () {
        applyPositions(boxes, false); // travel back home
        window.setTimeout(function () {
          section.classList.remove('awd-hp--shuffling');
          boxes.forEach(function (box) {
            // Hand control back to the CSS custom-property rules.
            box.el.style.removeProperty('left');
            box.el.style.removeProperty('top');
          });
          running = false;
          button.removeAttribute('disabled');
        }, MOVE_MS + 50);
      }, MOVE_MS + HOLD_MS);
    }

    button.addEventListener('click', run);
  }

  function initAll() {
    if (reduceMotion) return;
    if (window.innerWidth < DESKTOP_MIN) return;
    var sections = document.querySelectorAll('.awd-hp');
    Array.prototype.forEach.call(sections, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-bind when this section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    if (reduceMotion || window.innerWidth < DESKTOP_MIN) return;
    var el = e.target.querySelector
      ? e.target.querySelector('.awd-hp')
      : null;
    if (el) setup(el);
  });
})();
